import { Group, Select, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { TICKET_ZEITRAEUME, VORGANGSTYPEN, type SpiegelStand, type TicketZeitraum, type Vorgangstyp } from "@msc/shared";

import { formatBerlinDateTime } from "../../../lib/date.js";

/**
 * Kopf jedes Ticket-Blocks: Titel, Stand des Spiegels (orange ab doppeltem
 * Takt Verzug), Vorgangstyp und — nur für Blöcke ohne Monatsachse — Zeitraum.
 * Ohne Zeitraum-Auswahl gilt das Jahr der Seite.
 */
export function TicketBlockKopf({
  titel,
  hinweis,
  spiegel,
  vorgangstyp,
  onVorgangstyp,
  zeitraum,
  onZeitraum,
  zeitraumVorgabe = "jahr",
}: {
  titel: string;
  /** Untertitel, z. B. wenn Zeitraum und Typ nur einen Teil des Blocks betreffen. */
  hinweis?: string;
  spiegel: SpiegelStand | undefined;
  vorgangstyp: Vorgangstyp | null;
  onVorgangstyp: (v: Vorgangstyp | null) => void;
  zeitraum?: TicketZeitraum | null;
  onZeitraum?: (z: TicketZeitraum | null) => void;
  /** Was „keine Auswahl" bedeutet: das Jahr der Seite oder (Leads) letzte 12 Monate bzw. (Liegezeit) letzte 30 Tage. */
  zeitraumVorgabe?: "jahr" | "letzte_12_monate" | "letzte_30_tage";
}) {
  const { t } = useTranslation();
  return (
    <Group justify="space-between" align="flex-end" wrap="wrap" mb="xs">
      <div>
        <Text fw={600} size="sm">
          {titel}
        </Text>
        {hinweis && (
          <Text size="xs" c="dimmed">
            {hinweis}
          </Text>
        )}
        <Text size="xs" c={spiegel?.veraltet ? "orange.7" : "dimmed"}>
          {spiegel?.stand
            ? t("pages.kpi.tickets.stand", { zeit: formatBerlinDateTime(spiegel.stand) })
            : t("pages.kpi.tickets.standUnbekannt")}
        </Text>
      </div>
      <Group gap="xs">
        {onZeitraum && (
          <Select
            size="xs"
            aria-label={t("pages.kpi.tickets.zeitraumLabel", { block: titel })}
            value={zeitraum ?? "vorgabe"}
            onChange={(v) => onZeitraum(!v || v === "vorgabe" ? null : (v as TicketZeitraum))}
            allowDeselect={false}
            data={[
              { value: "vorgabe", label: t(`pages.kpi.tickets.zeitraum.${zeitraumVorgabe}`) },
              ...TICKET_ZEITRAEUME.filter((z) => z !== zeitraumVorgabe).map((z) => ({ value: z, label: t(`pages.kpi.tickets.zeitraum.${z}`) })),
            ]}
            style={{ minWidth: 170 }}
          />
        )}
        <Select
          size="xs"
          aria-label={t("pages.kpi.tickets.typLabel", { block: titel })}
          value={vorgangstyp ?? "alle"}
          onChange={(v) => onVorgangstyp(!v || v === "alle" ? null : (v as Vorgangstyp))}
          allowDeselect={false}
          data={[{ value: "alle", label: t("pages.kpi.tickets.typAlle") }, ...VORGANGSTYPEN.map((v) => ({ value: v, label: v }))]}
          style={{ minWidth: 150 }}
        />
      </Group>
    </Group>
  );
}
